import React, { lazy } from "react";

import { Navigate, useParams } from "react-router-dom";
import {
  CAR_REAL_ESTATE_AGENT_TYPE,
  IS_TAILWIND_COMPONENT,
  ROLES,
  SERVICE_PROVIDER_PORTAL_ROLES,
} from "../components/helpers/constants";
import {
  decryptPayload,
  findAllowedAttorneyPrefix,
  isAndroidAppWebView,
  isNativeAppShell,
} from "../components/helpers/utils";

// Route page components — lazy-loaded to keep main.js smaller
const Spouse = lazy(() => import("../components/spouse"));
const Login = lazy(() => import("../components/login/index"));
const LeadPage = lazy(() => import("../components/lead-page"));
const Admins = lazy(() => import("../components/admin/Admins"));
const EPNews = lazy(() => import("../components/News/index.js"));
const Scans = lazy(() => import("../components/Scans/index.tsx"));
const LogsTable = lazy(() => import("../components/customer-logs"));
const Homepage = lazy(() => import("../components/lennar/index.js"));
const Providers = lazy(() => import("../components/admin/providers"));
const UserSignup = lazy(() => import("../components/user-signup/index"));
const Documents = lazy(() => import("../components/userDocuments/index"));
const AdminLogsList = lazy(() => import("../components/admin/log-lists"));
const AuthCallback = lazy(() => import("../components/login/AuthCallback"));
const Communication = lazy(() => import("../components/Communication/index"));
const Properties = lazy(() => import("../components/userProfile/properties"));
const Subscriptions = lazy(() => import("../components/Subscriptions/index"));
const ForCustomer = lazy(() => import("../components/desktop/ForCustomer.js"));
const Press = lazy(() => import("../components/TailwindComponents/Press.tsx"));
const InvestorList = lazy(() => import("../components/investor/list/index.js"));
const Survey = lazy(() => import("../components/TailwindComponents/Survey.tsx"));
const UserSettingIndex = lazy(() => import("../components/userProfile/Settings"));
const Professional = lazy(() => import("../components/admin/professionals/index"));
const ServicersLogin = lazy(() => import("../components/Service-Provider/signIn"));
const CountyAdmin = lazy(() => import("../components/admin/CountyAdmin/index.js"));
const AttorneyPage = lazy(() => import("../components/attorney-program/index.js"));
const FrequentlyAskedQuestions = lazy(() => import("../components/FAQs/index.js"));
const AboutUs = lazy(() => import("../components/TailwindComponents/AboutUs.tsx"));
const Blog = lazy(() => import("../components/Blogs/TailwindComponents/Blog.tsx"));
const ServiceProviders = lazy(() => import("../components/admin/serviceProviders"));
const Professionals = lazy(() => import("../components/userProfile/Professionals"));
const CountyRecorder = lazy(() => import("../components/county-recorder/index.js"));
const SetupAccount = lazy(() => import("../components/setup-account/SetupAccount"));
const PartnerActivatePage = lazy(() =>
  import("../components/partner-activate/PartnerActivatePage"),
);
const JesseClaimPage = lazy(() => import("../components/jesse/JesseClaimPage.jsx"));
const TitleCompany = lazy(() => import("../components/admin/TitleCompany/index.js"));
const RemoveUserPage = lazy(() => import("../components/desktop/RemoveUserPage.js"));
const HowItWorksV2 = lazy(() => import("../components/howItsWorks/HowItWorksV2.js"));
const OrderSignPage = lazy(() => import("../components/orderSign/OrderSignPage.tsx"));
const DocumentRequest = lazy(() => import("../components/propertyRequestedDocuemnts"));
const InvestorPricing = lazy(() => import("../components/pricing/InvestorPricing.js"));
const AttorneyPricing = lazy(() => import("../components/pricing/AttorneyPricing.js"));
const AdminUsersHub = lazy(() => import("../components/admin/AdminUsersHub/index.js"));
const AdminSettings = lazy(() => import("../components/admin/AdminSettings/index.js"));
const AdminPartners = lazy(() => import("../components/admin/AdminPartners/index.js"));
const CarAdminDashboardPage = lazy(() => import("../components/admin/CarAdmin/CarAdminDashboardPage.js"));
const CarAdminAgentsPage = lazy(() => import("../components/admin/CarAdmin/CarAdminAgentsPage.js"));
const CarAdminAgentDetailPage = lazy(() => import("../components/admin/CarAdmin/CarAdminAgentDetailPage.js"));
const UserProfile = lazy(() => import("../components/userProfile/Dashboard/dashboard"));
const SendInvitation = lazy(() => import("../components/professional/send-invitation"));
const PaymentDone = lazy(() => import("../components/lennar/subscription/PaymentDone"));
const AgentAdmins = lazy(() => import("../components/attorney-program/Admins/index.js"));
const AdminMarketing = lazy(() => import("../components/admin/AdminMarketing/index.js"));
const ConvertLandingPage = lazy(() => import("../components/convert/ConvertLandingPage"));
const ServiceProviderSignup = lazy(() => import("../components/Service-Provider/signup"));
const RequestDocumentList = lazy(() => import("../components/admin/requestDocumentList"));
const AdminReportsHub = lazy(() => import("../components/admin/AdminReportsHub/index.js"));
const CustomerServiceQueue = lazy(() => import("../components/admin/customerServiceQueue"));
const AdminProfile = lazy(() => import("../components/admin/AdminDashboard/Admin-Profile"));
const AdminLegacyRedirect = lazy(() => import("../components/admin/AdminLegacyRedirect.js"));
const OwnerVerifyPage = lazy(() => import("../components/car/OwnerVerifyPage"));
const CarInviteDeclinePage = lazy(() => import("../components/car/CarInviteDeclinePage"));
const CarInviteViewPage = lazy(() => import("../components/car/CarInviteViewPage"));
const CarInviteCheckoutPage = lazy(() => import("../components/car/CarInviteCheckoutPage"));
const CreateNewPass = lazy(() => import("../components/create-reset-password/createNewPass"));
const ResetPassword = lazy(() => import("../components/create-reset-password/resetPassword"));
const LennarCreateAccount = lazy(() => import("../components/lennar/create-account/index.js"));
const PrivacyPolicy = lazy(() => import("../components/TailwindComponents/PrivacyPolicy.tsx"));
const AdminSpFulfillment = lazy(() => import("../components/admin/AdminSpFulfillment/index.js"));
const AndroidUserProfile = lazy(() => import("../components/userProfile/AndroidUserProfile.js"));
const TermsOfService = lazy(() => import("../components/TailwindComponents/TermsOfService.tsx"));
const AgentLoginScreen = lazy(() => import("../components/attorney-program/AgentLoginScreen.js"));
const ProfessionalsProfile = lazy(() => import("../components/professional/Professionals-Profile"));
const LeadGenDesktop = lazy(() => import("../components/PropertyRiskManagement/LeadGenDesktop.js"));
const AdminDashboardGate = lazy(() => import("../components/admin/AdminDashboard/AdminDashboardGate.js"));
const AdminRiskAssessments = lazy(() => import("../components/admin/AdminRiskAssessments/index.js"));
const OutreachPage = lazy(() => import("../components/admin/Outreach/index.js"));
const DocumentVerifyPage = lazy(() => import("../components/documentVerify/DocumentVerifyPage.tsx"));
const CmsPublicPage = lazy(() => import("../components/Blogs/TailwindComponents/CmsPublicPage.tsx"));
const AddUserDetailsFromMail = lazy(() => import("../components/desktop/AddUserDetailsFromMail.js"));
const ElDoradoCountyPage = lazy(() => import("../components/county-recorder/ElDoradoCountyPage.js"));
const RiskForInvestors = lazy(() => import("../components/TailwindComponents/RiskForInvestors.tsx"));
const LennarPropertyList = lazy(() => import("../components/lennar/subscription/LennarPropertyList"));
const BrokerSignupPage = lazy(() =>
  import("../components/attorney-program/BrokerSignupPage.js").then((m) => ({ default: m.BrokerSignupPage })),
);
const PricingDiversification = lazy(() => import("../components/pricing/PricingDiversification.tsx"));
const RealEstatePartnerships = lazy(() => import("../components/convert/RealEstatePartnerships.tsx"));
const CancellationPolicy = lazy(() => import("../components/lennar/subscription/CancellationPolicy "));
const RiskForHomeowners = lazy(() => import("../components/TailwindComponents/RiskForHomeowners.tsx"));
const RiskForInvestorsA = lazy(() => import("../components/TailwindComponents/RiskForInvestorsA.tsx"));
const ServicerLogsPage = lazy(() => import("../components/for-service-provider/ServicerLogsPage.jsx"));
const ProfessionalProperties = lazy(() => import("../components/professional/professional-properties"));
const CmsBlogPostPage = lazy(() => import("../components/Blogs/TailwindComponents/CmsBlogPostPage.tsx"));
const PropertyRiskReport = lazy(() =>
  import("../components/county-recorder/PropertyRiskReport.js").then((m) => ({ default: m.PropertyRiskReport })),
);
const RiskForHomeownersA = lazy(() => import("../components/TailwindComponents/RiskForHomeownersA.tsx"));
const ServicerScansPage = lazy(() => import("../components/for-service-provider/ServicerScansPage.jsx"));
const ProfessionalUsersList = lazy(() => import("../components/professional/users/ProfessionalUsersList"));
const ReportResultsInvestor = lazy(() => import("../components/county-recorder/ReportResultsInvestor.js"));
const WhatIsTitleTheftV2A = lazy(() => import("../components/TailwindComponents/WhatIsTitleTheftV2A.tsx"));
const TitleLockProtection = lazy(() => import("../components/TailwindComponents/TitleLockProtection.tsx"));
const RequestDocumentForm = lazy(() => import("../components/for-service-provider/RequestDocumentForm.js"));
const GenericLandingPage = lazy(() => import("../components/PropertyRiskManagement/GenericLandingPage.js"));
const MonitoringActivated = lazy(() => import("../components/county-recorder/FreeMonitoringActivated.tsx"));
const ServicerProfilePage = lazy(() => import("../components/for-service-provider/ServicerProfilePage.jsx"));
const ServicerClientsEntry = lazy(() => import("../components/for-service-provider/ServicerClientsEntry.jsx"));
const CarPortfolioRedirect = lazy(() =>
  import("../components/for-service-provider/ServicerClientsEntry.jsx").then(
    (module) => ({ default: module.CarPortfolioRedirect }),
  ),
);
const CarAgentPortfolioPage = lazy(() => import("../components/for-service-provider/CarAgentPortfolioPage.jsx"));
const CarClosingAssignmentsPage = lazy(() => import("../components/for-service-provider/CarClosingAssignmentsPage.jsx"));
const CarEscrowAccessPage = lazy(() => import("../components/for-service-provider/CarEscrowAccessPage.jsx"));
const CarAgentPortfolioDetailPage = lazy(() => import("../components/for-service-provider/CarAgentPortfolioDetailPage.jsx"));
const LeadGenShortVersion = lazy(() => import("../components/PropertyRiskManagement/LeadGenShortVersion.js"));
const CountyRecorderPartnerships = lazy(() => import("../components/convert/CountyRecorderPartnerships.tsx"));
const CarMemberPartnership = lazy(() => import("../components/convert/CarMemberPartnership.tsx"));
const ProfessionalDashboard = lazy(() => import("../components/professional/dashboard/ProfessionalDashboard"));
const SellingRefinancing = lazy(() => import("../components/userProfile/TaiwinComponents/SellingRefinancing"));
const ServicerDocumentsPage = lazy(() => import("../components/for-service-provider/ServicerDocumentsPage.jsx"));
const SellingRefinancingPage = lazy(() => import("../components/TailwindComponents/SellingRefinancingPage.tsx"));
const WhatIsHomeTitleTheft = lazy(() => import("../components/homeTitleTheftProtection/WhatIsHomeTitleTheft.js"));
const ServicerSearchProperty = lazy(() => import("../components/for-service-provider/ServicerSearchProperty.jsx"));
const ServicerPropertiesPage = lazy(() => import("../components/for-service-provider/ServicerPropertiesPage.jsx"));
const WhyMonitoringIsntEnough = lazy(() => import("../components/TailwindComponents/WhyMonitoringIsntEnough.tsx"));
const ServicerAllowAccessPage = lazy(() => import("../components/for-service-provider/ServicerAllowAccessPage.jsx"));
const ServicerCommissionsPage = lazy(() => import("../components/for-service-provider/ServicerCommissionsPage.jsx"));
const ServicerCreateNewPass = lazy(() => import("../components/Service-Provider/create-reset-password/createNewPass"));
const ServicerResetPassword = lazy(() => import("../components/Service-Provider/create-reset-password/resetPassword"));
const ServicerClientCreatePage = lazy(() => import("../components/for-service-provider/ServicerClientCreatePage.jsx"));
const NationalHostsLandingPage = lazy(() => import("../components/PropertyRiskManagement/NationalHostsLandingPage.js"));
const ServicerSubscriptionsPage = lazy(() => import("../components/for-service-provider/ServicerSubscriptionsPage.jsx"));
const PropertyProtectionScorecard = lazy(() => import("../components/TailwindComponents/PropertyProtectionScorecard.tsx"));
const HomeTitleProtectionLongTime = lazy(() => import("../components/TailwindComponents/HomeTitleProtectionLongTime.tsx"));
const ServicerRiskAssessmentsPage = lazy(() => import("../components/for-service-provider/ServicerRiskAssessmentsPage.jsx"));
const ServicerClientWorkspacePage = lazy(() => import("../components/for-service-provider/ServicerClientWorkspacePage.jsx"));
const ServicerJesseProtectedSearch = lazy(() => import("../components/for-service-provider/ServicerJesseProtectedSearch.jsx"));
const ServicerPropertyRequestsPage = lazy(() => import("../components/for-service-provider/ServicerPropertyRequestsPage.jsx"));
const ServicerLegacyClientsRedirect = lazy(() => import("../components/for-service-provider/ServicerLegacyClientsRedirect.jsx"));
const TitleProtectionForHomeOwners = lazy(() => import("../components/homeTitleTheftProtection/TitleProtectionForHomeOwners.js"));
const ServicerRequestedDocumentsPage = lazy(() => import("../components/for-service-provider/ServicerRequestedDocumentsPage.jsx"));
const HomeTitleMonitoringNotifications = lazy(() => import("../components/TailwindComponents/HomeTitleMonitoringNotifications.tsx"));
const ServicerFulfillmentAssignmentsPage = lazy(() => import("../components/for-service-provider/ServicerFulfillmentAssignmentsPage.jsx"));
const ServicerFulfillmentUccWorkspacePage = lazy(() => import("../components/for-service-provider/ServicerFulfillmentUccWorkspacePage.jsx"));
const TitleProtectionForRealEstateInvestors = lazy(() => import("../components/homeTitleTheftProtection/TitleProtectionForRealEstateInvestors.js"));
const IndustryPartnerWhatIsTitleTheftRedirect = lazy(() => import("../components/homeTitleTheftProtection/IndustryPartnerWhatIsTitleTheftRedirect.js"));
const Q2PropertyProtectionScorecardPressRelease = lazy(() => import("../components/Blogs/TailwindComponents/Q2PropertyProtectionScorecardPressRelease.tsx"));
const InvestorLogin = lazy(
  () => import("../components/investor/login/index.js"),
);
const CreativeLandingPage = lazy(
  () => import("../components/desktop/CreativeLandingPage.js"),
);
const Pricing = lazy(() => import("../components/pricing"));
const PrivacyPolicyPage = lazy(() => import("../components/privacy-policy"));
const ForServiceProvider = lazy(
  () => import("../components/for-service-provider"),
);
const SiteMapPage = lazy(() => import("../components/desktop/SiteMap"));

// Resolves /:countyAllowed prefix against CountyVertical + static attorney/industrypartner values
const ProgramPrefixedRoute = ({ Component, isEpRoute = false }) => {
  const { countyAllowed } = useParams();
  const match = findAllowedAttorneyPrefix(countyAllowed);
  if (!match) return <Component isEpRoute={true} />;
  if (match.isAttorney) return <Component isAttorneyRoute={true} />;
  if (match.isIndustryPartner)
    return <Component isIndustryPartnerRoute={true} />;
  return <Component linkPrefix={`/${countyAllowed}`} />;
};

const SUBDOMAIN = process.env.REACT_APP_SUBDOMAIN;
const JESSE_REDACT_ENABLED =
  String(process.env.REACT_APP_JESSE_REDACT_ENABLED || "").toLowerCase() ===
  "true";
const is_scrp_hp = localStorage.getItem("scrp_hp");
const isTailwindComponent = IS_TAILWIND_COMPONENT;
const isAndroidApp =
  typeof window !== "undefined" &&
  (isAndroidAppWebView() ||
    decryptPayload(localStorage.getItem("isMobile")) === "android");
// Hide marketing chrome in the iOS/Android app shell (Android already did this;
// iOS was still showing the public header with a logo link to `/`).
const hideNativeAppChrome =
  typeof window !== "undefined" && (isAndroidApp || isNativeAppShell());

export const publicRoutesV2 = [
  // {
  //   path: "/",
  //   component: <CreativeLandingPage isTailwindComponent={isTailwindComponent} />,
  //   title: "Home",
  // },
  // {
  //   path: "/equity-protect",
  //   component: <CreativeLandingPage isTailwindComponent={isTailwindComponent} />,
  //   title: "Home",
  // },
  {
    path: "/login",
    component: <Login isTailwindComponent={isTailwindComponent} />,
    title: "Login ",
    hideNavbar: true,
    hideFooter: true,
  },
  {
    path: "/auth/callback",
    component: <AuthCallback />,
    title: "Social Sign-In",
    hideNavbar: true,
    hideFooter: true,
    hideFromSitemap: true,
  },
  {
    path: "/:prefix/login",
    component: <Login isTailwindComponent={isTailwindComponent} />,
    title: "Login ",
    hideNavbar: true,
    hideFooter: true,
  },
  {
    path: "/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideNavbar: true,
    hideFooter: true,
    hideFromSitemap: true,
  },
  {
    path: "/partner-activate",
    component: <PartnerActivatePage />,
    title: "Activate Complete Protection",
    hideNavbar: true,
    hideFooter: true,
    hideFromSitemap: true,
  },
  {
    path: "/:prefix/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideNavbar: true,
    hideFooter: true,
    hideFromSitemap: true,
  },

  {
    path: "/create-account",
    component: <UserSignup isTailwindComponent={isTailwindComponent} />,
    title: "Create Account ",
    hideNavbar: hideNativeAppChrome,
    // hideFromSitemap: true,
  },
  {
    path: "/:county_name/create-account",
    component: <UserSignup isTailwindComponent={isTailwindComponent} />,
    title: "Create Account ",
    hideFromSitemap: true,
    hideNavbar: hideNativeAppChrome,
  },
  {
    path: "/create-account/*",
    component: <UserSignup isTailwindComponent={isTailwindComponent} />,
    title: "Create Account ",
    hideFromSitemap: true,
    hideNavbar: hideNativeAppChrome,
  },
  {
    path: "/how-it-works",
    component: <HowItWorksV2 />,
    title: "How it Works ",
  },
  {
    path: ":prefix/:company/how-it-works",
    component: <HowItWorksV2 />,
    title: "How it Works ",
    hideFromSitemap: true,
  },
  {
    path: ":prefix/how-it-works",
    component: <HowItWorksV2 />,
    title: "How it Works ",
    hideFromSitemap: true,
  },
  {
    path: "/pricing",
    component: <PricingDiversification />,
    title: "Pricing ",
  },
  {
    path: "/selling-refinancing",
    // component: <PricingV2 />,
    component: <SellingRefinancingPage />,
    title: "Selling Refinancing ",
  },
  {
    path: "/property-risk-management",
    component: <LeadGenDesktop isTailwindComponent={isTailwindComponent} />,
    title: "Property Risk Assessment",
  },
  {
    path: ":prefix/property-risk-management",
    component: (
      <LeadGenDesktop
        company={false}
        isTailwindComponent={isTailwindComponent}
      />
    ),
    title: "Property Risk Report ",
    hideFromSitemap: true,
  },
  {
    path: "/property-risk-report",
    component: <PropertyRiskReport isTailwindComponent={isTailwindComponent} />,
    title: "Property Risk Report ",
    hideFromSitemap: true,
  },
  {
    path: "/property-monitoring-activated",
    component: (
      <MonitoringActivated isTailwindComponent={isTailwindComponent} />
    ),
    title: "Free Property Notifications Activated ",
    hideFromSitemap: true,
  },
  {
    path: "/what-is-home-title-theft",
    // component: <WhatIsTitleTheftV2 isEpRoute={true} />,
    component: <WhatIsTitleTheftV2A isEpRoute={true} />,
    title: "What Is Home Title Theft",
  },
  {
    path: "/title-protection-long-time-homeowners",
    component: <RiskForHomeowners isEpRoute={true} />,
    title: "Title Protection Long Time Homeowners",
  },
  {
    path: "/home-title-theft-risk-new-homeowners",
    component: <RiskForHomeownersA isEpRoute={true} />,
    title: "Title Protection Long Time Homeowners",
  },
  {
    path: "/title-protection-real-estate-investors",
    component: <RiskForInvestors isEpRoute={true} />,
    title: "Title Protection Real Estate Investors",
  },
  {
    path: "/title-theft-protection-real-estate-investors",
    component: <RiskForInvestorsA isEpRoute={true} />,
    title: "Title Protection Real Estate Investors",
  },
  {
    path: "/home-title-monitoring-notifications",
    component: <HomeTitleMonitoringNotifications isEpRoute={true} />,
    title: "Home Title Monitoring Notifications",
  },
  {
    path: "/title-lock-protection-for-your-home",
    component: <TitleLockProtection isEpRoute={true} />,
    title: "Title Lock Protection for Your Home",
  },
  {
    path: "/home-title-protection-long-time-homeowners",
    component: <HomeTitleProtectionLongTime isEpRoute={true} />,
    title: "Home Title Protection for Long Time Homeowners",
  },
  {
    path: "/why-monitoring-isnt-enough",
    component: <WhyMonitoringIsntEnough isEpRoute={true} />,
    title: "Why Monitoring Isn't Enough",
  },
  {
    path: "/:countyAllowed/what-is-home-title-theft",
    component: <ProgramPrefixedRoute Component={WhatIsTitleTheftV2A} />,
    title: "What Is Home Title Theft",
    headerProps: {
      excludeLinks: ["How We Help", "Legislative Map", "For Service Providers"],
      prefixNavLinks: true,
    },
  },
  {
    path: "/:countyAllowed/title-protection-long-time-homeowners",
    component: <ProgramPrefixedRoute Component={RiskForHomeownersA} />,
    title: "Title Protection Long Time Homeowners",
    headerProps: {
      excludeLinks: ["How We Help", "Legislative Map", "For Service Providers"],
      prefixNavLinks: true,
    },
  },
  {
    path: "/:countyAllowed/title-protection-real-estate-investors",
    component: <ProgramPrefixedRoute Component={RiskForInvestorsA} />,
    title: "Title Protection Real Estate Investors",
    headerProps: {
      excludeLinks: ["How We Help", "Legislative Map", "For Service Providers"],
      prefixNavLinks: true,
    },
  },
  {
    path: "/:countyAllowed/home-title-theft-risk-new-homeowners",
    component: <ProgramPrefixedRoute Component={RiskForHomeownersA} />,
    title: "Title Protection Long Time Homeowners",
    headerProps: {
      excludeLinks: ["How We Help", "Legislative Map", "For Service Providers"],
      prefixNavLinks: true,
    },
  },
  {
    path: "/:countyAllowed/title-theft-protection-real-estate-investors",
    component: <ProgramPrefixedRoute Component={RiskForInvestorsA} />,
    title: "Title Protection Real Estate Investors",
    headerProps: {
      excludeLinks: ["How We Help", "Legislative Map", "For Service Providers"],
      prefixNavLinks: true,
    },
  },
  {
    path: "/:countyAllowed/home-title-protection-long-time-homeowners",
    component: <ProgramPrefixedRoute Component={HomeTitleProtectionLongTime} />,
    title: "Home Title Protection for Long Time Homeowners",
    headerProps: {
      excludeLinks: ["How We Help", "Legislative Map", "For Service Providers"],
      prefixNavLinks: true,
    },
  },

  {
    path: "/real-estate-partnerships",
    component: <RealEstatePartnerships />,
    title: "Real Estate Professional Partnerships",
  },
  {
    path: "/county-recorder-partnerships",
    component: <CountyRecorderPartnerships />,
    title: "County Recorder Partnerships",
  },
  {
    path: "/about",
    component: <AboutUs />,
    title: "About Us",
  },
  {
    path: "/convert",
    component: <ConvertLandingPage />,
    title: "Activate Your Secure EquityProtect Dashboard",
    isLandingPage: true,
    metaDescription:
      "Create your secure EquityProtect account to access your dashboard, monitor your property's protection, view risk scans, manage alerts, and more.",
  },
  {
    path: "/press",
    component: <Press />,
    title: "Press",
  },
  {
    path: "/scorecard",
    component: <PropertyProtectionScorecard />,
    title: "Property Protection Scorecard",
  },
  {
    path: "/privacy",
    component: <PrivacyPolicy isAndroidApp={hideNativeAppChrome} />,
    title: "Privacy Policy",
    hideNavbar: hideNativeAppChrome,
  },
  {
    path: "/terms",
    component: <TermsOfService isAndroidApp={hideNativeAppChrome} />,
    title: "Terms & Conditions",
    hideNavbar: hideNativeAppChrome,
  },
  {
    path: "/survey",
    component: <Survey />,
    title: "Survey",
  },
  {
    path: "/verify/document/:token",
    component: <DocumentVerifyPage />,
    title: "Verify Document",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  ...(JESSE_REDACT_ENABLED
    ? [
        {
          path: "/jesse/claim/:token",
          component: <JesseClaimPage />,
          title: "Protected Party Registration",
          hideNavbar: true,
          footer: false,
          hideFromSitemap: true,
        },
      ]
    : []),
  {
    path: "/news",
    component: <EPNews isTailwindComponent={isTailwindComponent} />,
    title: "EquityProtect in the News",
  },
];

export const blogRoutesV2 = [
  {
    path: "/blog",
    component: <Blog />,
    title: "Blogs",
  },
  {
    path: "/blog/equityprotect-q2-2026-property-protection-scorecard",
    component: <Q2PropertyProtectionScorecardPressRelease />,
    title: "Q2 2026 Property Protection Scorecard",
  },
  {
    path: "/blog/:slug",
    component: <CmsBlogPostPage />,
    title: "Blog",
  },
  {
    path: "/pages/:slug",
    component: <CmsPublicPage />,
    title: "Page",
  },
];

export const publicRoutes = [
  {
    path: "/",
    component: (
      <CreativeLandingPage isTailwindComponent={isTailwindComponent} />
    ),
    title: "Home",
    isLandingPage: true,
    isInvestorsButton: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/equity-protect",
    component: (
      <CreativeLandingPage isTailwindComponent={isTailwindComponent} />
    ),
    title: "Home",
    isLandingPage: true,
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  // {
  //   path: "/how-it-works",
  //   component: <HowItWorks />,
  //   title: "How it Works ",
  // },

  // {
  //   path: ":prefix/:company/how-it-works",
  //   component: <HowItWorks />,
  //   title: "How it Works ",
  //   hideFromSitemap: true,
  // },
  // {
  //   path: ":prefix/how-it-works",
  //   component: <HowItWorks />,
  //   title: "How it Works ",
  //   hideFromSitemap: true,
  // },
  // {
  //   path: "/pricing",
  //   component: <Pricing />,
  //   title: "Pricing ",
  // },
  {
    path: ":prefix/:company/pricing",
    component: <Pricing />,
    title: "Pricing ",
    hideFromSitemap: true,
  },
  {
    path: "/investor-pricing",
    component: <InvestorPricing />,
    title: "Investor Pricing ",
    isInvestors: true,
    hide_my_acc_btn: true,
    isNavLinkLeftAlign: true,
  },
  {
    path: ":county_name/pricing",
    component: <Pricing />,
    title: "Pricing ",
    hideFromSitemap: true,
  },
  {
    path: "/:countyin",
    component: <InvestorLogin isCountyVertical={true} />,
    title: "County Login",
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
    isCountyVertical: true,
  },
  {
    path: "/househappy",
    component: (
      <AttorneyPage
        isAgent={true}
        base_route={"househappy"}
        hide_call_us_now={true}
        hide_my_acc_btn={false}
        only_prefix={true}
      />
    ),
    title: "House Happy",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  // {
  //   path: "/property-risk-management",
  //   component: <LeadGenDesktop />,
  //   title: "Property Risk Management",
  //   hideNavbar: true,
  //   hide_call_us_now: true,
  // },
  // {
  //   path: ":prefix/property-risk-management",
  //   component: <LeadGenDesktop company={false} />,
  //   title: "Property Risk Report ",
  //   hideNavbar: true,
  //   hideFromSitemap: true,
  // },
  {
    path: "/generic-host",
    component: <GenericLandingPage />,
    title: "Generic Host",
    hideNavbar: true,
  },
  {
    path: "/Equity-Guard",
    component: <GenericLandingPage />,
    title: "Equity Guard",
    hideNavbar: true,
  },
  {
    path: "/gallagher",
    component: <NationalHostsLandingPage radio={"Mike Gallagher"} />,
    title: "Property Risk Report",
    hideNavbar: true,
    hideFromSitemap: true,
  },
  {
    path: "/prager",
    component: <NationalHostsLandingPage radio={"Dennis Prager"} />,
    title: "Property Risk Report",
    hideNavbar: true,
    hideFromSitemap: true,
  },
  {
    path: "/gorka",
    component: <NationalHostsLandingPage radio={"Dr. Sebastian Gorka"} />,
    title: "Property Risk Report",
    hideNavbar: true,
    hideFromSitemap: true,
  },
  {
    path: "/county-recorder/eldorado",
    component: <ElDoradoCountyPage />,
    title: "El Dorado County Recorder",
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/county-recorder/:county_name",
    component: <CountyRecorder />,
    title: "County Recorder",
    hideFromSitemap: true,
  },
  {
    path: "/lead-gen",
    component: <LeadGenShortVersion />,
    title: "Lead Generator",
    hideFromSitemap: true,
    hideNavbar: false,
  },
  // {
  //   path: "/property-risk-report",
  //   title: "Property Risk Report",
  //   component: <PropertyRiskReport />,
  //   hideFromSitemap: true,
  // },
  {
    path: "/data-deletion",
    title: "Property Risk Report",
    component: <RemoveUserPage />,
    hideFromSitemap: true,
    hide_nav_links: true,
    hide_my_acc_btn: true,
    hide_call_us_now: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
  },
  {
    path: "/:prefix/property-risk-report",
    component: <PropertyRiskReport />,
    title: "Property Risk Report",
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  {
    path: "/investor",
    component: <InvestorLogin />,
    title: "Investor Login",
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/investors",
    component: <InvestorLogin />,
    title: "Investor Login",
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/investor-login",
    component: <InvestorLogin />,
    title: "Investor Login",
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/investor-login/:uuid",
    component: <InvestorLogin />,
    title: "Investor Login",
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/report-results-investor/:uuid",
    component: <ReportResultsInvestor />,
    title: "Report Results Investor",
    hideServiceProviderFromNavbar: true,
    hideFromSitemap: true,
    isLandingPage: true,
  },
  {
    path: "/investor-list/:uuid",
    component: <InvestorList />,
    title: "Investor's Property list",
    hideFromSitemap: true,
    hideNavbar: true,
    isInvestors: true,
    hide_my_acc_btn: true,
    isNavLinkLeftAlign: true,
    isSiteMap: false,
  },
  {
    path: "/:countyin/properties/:uuid",
    component: <InvestorList isTailwindComponent={isTailwindComponent} />,
    title: "County Investor's Property list",
    hideFromSitemap: true,
    hideNavbar: true,
    isInvestors: true,
    hide_my_acc_btn: true,
    isNavLinkLeftAlign: true,
    isSiteMap: false,
    footer: !isTailwindComponent,
  },
  {
    path: "/attorney-title-program",
    component: <AttorneyPage isAttorneyRoute={true} />,
    title: "Attorney program",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_call_us_now: false,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
  },
  {
    path: "/attorney-title-program/pricing",
    component: <AttorneyPricing isAttorneyRoute={true} />,
    title: "Pricing ",
    isAttorneyRoute: true,
    hideServiceProviderFromNavbar: true,
    hide_my_acc_btn: true,
  },
  {
    path: "/attorney-title-program/premier-protection",
    component: <CreativeLandingPage isPremierProtection={true} />,
    title: "What is Premier Protection",
    isLandingPage: true,
    hideServiceProviderFromNavbar: true,
    isAttorneyRoute: true,
    hide_my_acc_btn: true,
  },
  {
    path: "/attorney-title-program/title-protection-real-estate-investors",
    component: <TitleProtectionForRealEstateInvestors isAttorneyRoute={true} />,
    title: "Title Protection Real Estate Investors",
    hideNavbar: true,
    isAttorneyRoute: true,
  },
  {
    path: "/attorney-title-program/title-protection-long-time-homeowners",
    component: <TitleProtectionForHomeOwners isAttorneyRoute={true} />,
    title: "Title Protection Long Time Homeowners",
    hideNavbar: true,
    isAttorneyRoute: true,
  },
  {
    path: "/attorney-title-program/what-is-home-title-theft",
    component: <WhatIsHomeTitleTheft isAttorneyRoute={true} />,
    title: "What Is Home Title Theft",
    hideNavbar: true,
    isAttorneyRoute: true,
  },
  {
    path: "/industrypartner",
    component: <AttorneyPage isIndustryPartnerRoute={true} />,
    title: "Industry Partner",
    isLandingPage: true,
    isIndustryPartnerRoute: true,
    isSiteMap: false,
    hide_call_us_now: false,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
  },
  {
    path: "/industrypartner/pricing",
    component: <AttorneyPricing />,
    title: "Pricing ",
    isIndustryPartnerRoute: true,
    hideServiceProviderFromNavbar: true,
    hide_my_acc_btn: true,
  },
  {
    path: "/industrypartner/premier-protection",
    component: <CreativeLandingPage isPremierProtection={true} />,
    title: "What is Premier Protection",
    isLandingPage: true,
    hideServiceProviderFromNavbar: true,
    isIndustryPartnerRoute: true,
    hide_my_acc_btn: true,
  },
  {
    path: "/industrypartner/title-protection-real-estate-investors",
    component: (
      <TitleProtectionForRealEstateInvestors isIndustryPartnerRoute={true} />
    ),
    title: "Title Protection Real Estate Investors",
    hideNavbar: true,
    isIndustryPartnerRoute: true,
  },
  {
    path: "/industrypartner/title-protection-long-time-homeowners",
    component: <TitleProtectionForHomeOwners isIndustryPartnerRoute={true} />,
    title: "Title Protection Long Time Homeowners",
    hideNavbar: true,
    isIndustryPartnerRoute: true,
  },
  {
    path: "/industrypartner/what-is-home-title-theft",
    component: <IndustryPartnerWhatIsTitleTheftRedirect />,
    title: "What Is Home Title Theft",
    hideNavbar: true,
    hideFromSitemap: true,
    isIndustryPartnerRoute: true,
  },
  // {
  //   path: "/title-protection-real-estate-investors",
  //   component: <TitleProtectionForRealEstateInvestors isEpRoute={true} />,
  //   title: "Title Protection Real Estate Investors",
  //   hideNavbar: true,
  // },
  // {
  //   path: "/title-protection-long-time-homeowners",
  //   component: <TitleProtectionForHomeOwners isEpRoute={true} />,
  //   title: "Title Protection Long Time Homeowners",
  //   hideNavbar: true,
  // },
  // {
  //   path: "/what-is-home-title-theft",
  //   component: <WhatIsHomeTitleTheft isEpRoute={true} />,
  //   title: "What Is Home Title Theft",
  //   hideNavbar: true,
  // },
  {
    path: "/for-service-provider",
    component: <ForServiceProvider isTailwindComponent={isTailwindComponent} />,
    title: "Go To  Service Provider",
    hideFromSitemap: is_scrp_hp ? true : false,
    hideNavbar: true,
  },
  // {
  //   path: "/news",
  //   component: <EPNews />,
  //   title: "EquityProtect in the News",
  // },
  {
    path: "/faqs",
    component: <FrequentlyAskedQuestions />,
    title: "Frequently Asked Questions",
    stickyNavbar: true,
  },
  // {
  //   path: "/privacy",
  //   component: <PrivacyPolicyPage />,
  //   title: "Privacy Policy",
  // },
  // {
  //   path: "/terms",
  //   component: <TermsAndConditions />,
  //   title: "Terms & Conditions",
  //   hideFromSitemap: true,
  // },
  {
    path: "/sitemap",
    component: <SiteMapPage />,
    title: "Sitemap",
    hideFromSitemap: true,
  },
  // INFO: Broker vertical dynamic routes
  {
    path: "/broker/:broker",
    component: (
      <AttorneyPage
        isAgent={true}
        base_route={"broker"}
        hide_call_us_now={true}
        hide_my_acc_btn={false}
      />
    ),
    title: "Brokers",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  {
    path: "/broker/:broker/agent-create-account",
    component: <BrokerSignupPage isAgent={true} base_route={"broker"} />,
    title: "Broker Agent's Create Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/broker/:broker/agent-login",
    component: <AgentLoginScreen base_route={"broker"} />,
    title: "Broker Agent's Login",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/broker/:broker/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/broker/:broker/re-agents",
    // component: <BrokerProperties base_route={"broker"} />,
    component: <AgentAdmins base_route={"broker"} isREAgent={true} />,
    title: "Broker Agent's Properties",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  // INFO: Title without prefix vertical dynamic routes
  {
    path: "/:title_prefix",
    component: (
      <AttorneyPage
        isAgent={true}
        base_route={"title"}
        hide_call_us_now={true}
        hide_my_acc_btn={false}
        only_prefix={true}
        company_name_as_prefix={true}
      />
    ),
    title: "Title",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  // {
  //   path: "/:title_prefix/agent-create-account",
  //   component: <BrokerSignupPage isAgent={true} base_route={"title"} />,
  //   title: "Title Agent's Create Account",
  //   hideNavbar: true,
  //   footer: false,
  //   hideFromSitemap: true,
  // },
  {
    path: "/:title_prefix/agent-login",
    component: <AgentLoginScreen base_route={"title"} isAssistant={true} />,
    title: "Title Agent's Login",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/:title_prefix/assistant",
    component: (
      <AgentAdmins base_route={"title"} isREAgent={false} isAssistant={true} />
    ),
    title: "Title's Assistants",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/:title_prefix/agent-admin",
    component: (
      <AgentAdmins
        base_route={"title"}
        isAgentAdmin={true}
        isAssistant={true}
      />
    ),
    title: "Title Admin",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  // INFO: Title vertical dynamic routes
  {
    path: "/title/:broker",
    component: (
      <AttorneyPage
        isAgent={true}
        base_route={"title"}
        hide_call_us_now={true}
        hide_my_acc_btn={false}
      />
    ),
    title: "Title",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/agent-create-account",
    component: <BrokerSignupPage isAgent={true} base_route={"title"} />,
    title: "Title Agent's Create Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/agent-login",
    component: <AgentLoginScreen base_route={"title"} />,
    title: "Title Agent's Login",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/re-agents",
    // component: <BrokerProperties base_route={"title"} />,
    component: <AgentAdmins base_route={"title"} isREAgent={true} />,
    title: "Title Agents's Properties",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/agent-admin",
    component: <AgentAdmins base_route={"title"} isAgentAdmin={true} />,
    title: "Title Admin",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/broker/:broker/agent-admin",
    component: <AgentAdmins base_route={"broker"} isAgentAdmin={true} />,
    title: "Broker Admin",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/sales-manager",
    component: <AgentAdmins base_route={"title"} isSalesManager={true} />,
    title: "Title's Admin Sales Manager",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/broker/:broker/sales-manager",
    component: <AgentAdmins base_route={"title"} isSalesManager={true} />,
    title: "Broker's Admin Sales Manager",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/title/:broker/representative",
    component: <AgentAdmins base_route={"title"} isRepresentative={true} />,
    title: "Title's Sales Representativer",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  // INFO: Insurance vertical dynamic routes
  {
    path: "/insurance/:broker",
    component: (
      <AttorneyPage
        isAgent={true}
        base_route={"insurance"}
        hide_call_us_now={true}
        hide_my_acc_btn={false}
      />
    ),
    title: "Insurance",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/agent-create-account",
    component: <BrokerSignupPage isAgent={true} base_route={"insurance"} />,
    title: "Insurance Agent's Create Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/agent-login",
    component: <AgentLoginScreen base_route={"insurance"} />,
    title: "Insurance Agent's Login",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/re-agents",
    // component: <BrokerProperties base_route={"insurance"} />,
    component: <AgentAdmins base_route={"insurance"} isREAgent={true} />,
    title: "Insurance Agents's Properties",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/agent-admin",
    component: <AgentAdmins base_route={"insurance"} isAgentAdmin={true} />,
    title: "Insurance Admin",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/sales-manager",
    component: <AgentAdmins base_route={"insurance"} isSalesManager={true} />,
    title: "Insurance's Admin Sales Manager",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/representative",
    component: <AgentAdmins base_route={"insurance"} isRepresentative={true} />,
    title: "Insurance's Sales Representative",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/property-risk-management",
    component: <LeadGenDesktop base_route={"insurance"} company={true} />,
    title: "Insurance's Property Risk Assesment",
    isAttorneyRoute: true,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  {
    path: "/insurance/:broker/property-risk-report",
    component: <PropertyRiskReport base_route={"insurance"} />,
    title: "Insurance's Property Risk Report",
    // isAttorneyRoute: true,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  // INFO: Broker vertical dynamic routes (continued)
  {
    path: "/broker/:broker/representative",
    component: <AgentAdmins base_route={"broker"} isRepresentative={true} />,
    title: "Broker's Sales Representative",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  // INFO: Attorney vertical dynamic routes
  {
    path: "/attorney/:broker",
    component: (
      <AttorneyPage
        isAgent={true}
        base_route={"attorney"}
        hide_call_us_now={true}
        hide_my_acc_btn={false}
      />
    ),
    title: "Attorney",
    isLandingPage: true,
    isAttorneyRoute: true,
    isSiteMap: false,
    hide_my_acc_btn: true,
    hideServiceProviderFromNavbar: true,
    hideNavbar: true,
    hide_footerlinks: true,
    hide_footer_CTA: true,
    hideFromSitemap: true,
  },
  {
    path: "/attorney/:broker/attorney-create-account",
    component: <BrokerSignupPage base_route={"attorney"} isSignup={true} />,
    title: "Attorney Agent's Create Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/attorney/:broker/attorney-login",
    component: <AgentLoginScreen base_route={"attorney"} />,
    title: "Attorney Agent's Login",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/attorney/:broker/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/attorney/:broker/attorneys",
    // component: <BrokerProperties base_route={"broker"} />,
    component: <AgentAdmins base_route={"attorney"} isREAgent={true} />,
    title: "Attorney Agent's Properties",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
  {
    path: "/attorney/:broker/attorney-admin",
    component: <AgentAdmins base_route={"attorney"} isAgentAdmin={true} />,
    title: "Attorney Admin",
    hideNavbar: true,
    footer: false,
    hideFromSitemap: true,
  },
];

export const userAdminRoutes = [
  // {
  //   path: "/security-Info",
  //   component: <Demo />,
  //   title: "Demo ",
  // },
  // {
  //   path: "/create-account-v2",
  //   component: <UserSignup />,
  //   title: "Create Account ",
  //   // hideFromSitemap: true,
  // },
  // {
  //   path: "/:county_name/create-account-v2",
  //   component: <UserSignup />,
  //   title: "Create Account ",
  //   hideFromSitemap: true,
  // },
  // {
  //   path: "/create-account-v2/*",
  //   component: <UserSignup />,
  //   title: "Create Account ",
  //   hideFromSitemap: true,
  // },
  // {
  //   path: "/create-agent-account",
  //   component: <AgentSignup />,
  //   title: "Create Agent Account ",
  // },
  // {
  //   path: "/login",
  //   component: <Login />,
  //   title: "Login ",
  //   hideLoginURL: true,
  // },
  // {
  //   path: "/:prefix/login",
  //   component: <Login />,
  //   title: "Login ",
  //   hideLoginURL: true,
  //   hideFromSitemap: true,
  // },
  {
    path: "/verify-identity/:sessionId",
    component: <OwnerVerifyPage />,
    title: "Verify your identity",
    hideNavbar: true,
    hideFromSitemap: true,
    footer: false,
  },
  {
    // Decline link in a CAR agent's free invitation email (card 3841).
    path: "/car-invite/decline",
    component: <CarInviteDeclinePage />,
    title: "Invitation declined",
    hideNavbar: true,
    hideFromSitemap: true,
    footer: false,
  },
  {
    // "See the Risk for Your Home" link in the same email.
    path: "/car-invite/view",
    component: <CarInviteViewPage />,
    title: "Your property risk assessment",
    hideNavbar: true,
    hideFromSitemap: true,
    footer: false,
  },
  {
    path: "/create-new-password",
    component: <CreateNewPass isTailwindComponent={isTailwindComponent} />,
    title: "Create New Password ",
    hideFromSitemap: true,
  },
  {
    path: "/:prefix/create-new-password",
    component: <CreateNewPass isTailwindComponent={isTailwindComponent} />,
    title: "Create New Password ",
    hideFromSitemap: true,
  },
  {
    path: "/reset-password",
    component: <ResetPassword isTailwindComponent={isTailwindComponent} />,
    title: "Forgot Password ",
    // hideFromSitemap: true,
  },
  {
    path: "/:prefix/reset-password",
    component: <ResetPassword isTailwindComponent={isTailwindComponent} />,
    title: "Forgot Password ",
    hideFromSitemap: true,
  },
  // {
  //   path: "/verify-otp-password",
  //   component: <VerifyOTP />,
  //   title: "Reset Password ",
  // },
  {
    path: "/add-user-details/:userToken",
    component: <AddUserDetailsFromMail />,
    title: "Add User Details",
    hideFromSitemap: true,
    footer: true,
    hideLoginURL: true,
  },
  {
    path: "/android-user-profile",
    component: <AndroidUserProfile />,
    title: "Add User Details",
    hideFromSitemap: true,
    hideNavbar: true,
    hideLoginURL: true,
  },
];

export const loggedInUserRoutes = [
  {
    path: "/user-profile",
    component: <UserProfile isTailwindComponent={isTailwindComponent} />,
    title: "User Profile",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/user-profile/selling-refinancing",
    component: <SellingRefinancing isTailwindComponent={isTailwindComponent} />,
    title: "User Profile",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/subscriptions",
    component: <Subscriptions isTailwindComponent={isTailwindComponent} />,
    title: "Subscriptions",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    // "Protect My Home" on the CAR invitation risk card.
    path: "/car-invite/checkout",
    component: <CarInviteCheckoutPage />,
    title: "Protect Your Home",
    role: [ROLES.customer],
  },
  {
    path: "/properties",
    component: (
      <Properties
        isTailwindComponent={isTailwindComponent}
        roles={[ROLES?.customer, ROLES?.secondaryUser]}
      />
    ),
    title: "Properties",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/scans",
    component: <Scans isTailwindComponent={isTailwindComponent} />,
    title: "Scans",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  // {
  //   path: "/market-value",
  //   component: <MarketValue />,
  //   title: "Properties",
  //   role: [ROLES.customer, ROLES.secondaryUser],
  // },
  {
    path: "/documents",
    component: <Documents isTailwindComponent={isTailwindComponent} />,
    title: "Documents",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/order-sign",
    component: <OrderSignPage />,
    title: "Order Sign",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/communication",
    component: <Communication isTailwindComponent={isTailwindComponent} />,
    title: "Communication",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/sub-user",
    component: <Spouse isTailwindComponent={isTailwindComponent} />,
    title: "Allow Access",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/professionals",
    component: <Professionals />,
    title: "Professionals",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  {
    path: "/settings",
    component: <UserSettingIndex isTailwindComponent={isTailwindComponent} />,
    title: "User Settings",
    role: [ROLES.customer, ROLES.secondaryUser],
  },
  // {
  //   path: "/logs",
  //   component: <LogsTable />,
  //   title: "Logs",
  //   role: [ROLES.customer, ROLES.secondaryUser],
  // },
  // {
  //   path: "/requested-documents",
  //   component: <DocumentRequest />,
  //   title: "Requested Documents",
  //   role: [ROLES.customer, ROLES.secondaryUser],
  // },
];

export const adminRoutes = [
  {
    path: "/admin-dashboard",
    component: <AdminDashboardGate />,
    title: "Admin Dashboard",
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.basicAccess,
      ROLES.marketingAdmin,
      ROLES.countyRecorder,
    ],
  },
  {
    path: "/admin/users",
    component: <AdminUsersHub />,
    title: "Admin Users Hub",
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.basicAccess,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin/risk-assessments",
    component: <AdminRiskAssessments />,
    title: "Risk Assessments",
    role: [ROLES.admin, ROLES.subAdmin, ROLES.countyRecorder],
  },
  {
    path: "/admin/outreach/*",
    component: <OutreachPage />,
    title: "EP Outreach",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.countyRecorder],
  },
  {
    path: "/admin/sp-fulfillment",
    component: <AdminSpFulfillment />,
    title: "SP Territory Fulfillment",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin/reports",
    component: <AdminReportsHub />,
    title: "Admin Reports",
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.basicAccess,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin/users/create",
    component: <AdminUsersHub mode="create" />,
    title: "Create User",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin/users/:propertyUserID",
    component: <AdminUsersHub />,
    title: "Admin User Detail",
    hideFromSitemap: true,
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.basicAccess,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin/partners",
    component: <AdminPartners />,
    title: "Admin Partners",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin/car/dashboard",
    component: <CarAdminDashboardPage />,
    title: "C.A.R. Dashboard",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.countyRecorder],
  },
  {
    path: "/admin/car/agents",
    component: <CarAdminAgentsPage />,
    title: "C.A.R. Agents",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.countyRecorder],
  },
  {
    path: "/admin/car/agents/:agentId",
    component: <CarAdminAgentDetailPage />,
    title: "C.A.R. Agent",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.countyRecorder],
  },
  {
    path: "/admin/marketing",
    component: <AdminMarketing />,
    title: "Admin Marketing",
    role: [ROLES.admin, ROLES.subAdmin, ROLES.marketingAdmin],
  },
  {
    path: "/admin/settings",
    component: <AdminSettings />,
    title: "Admin Settings",
    role: [ROLES.admin, ROLES.subAdmin, ROLES.basicAccess],
  },
  {
    path: "/admin/internal",
    component: <Admins />,
    title: "Internal Admins",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-users",
    component: <AdminLegacyRedirect />,
    title: "Admin Users",
    hideFromSitemap: true,
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.basicAccess,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin-users/:propertyUserID",
    component: <AdminLegacyRedirect />,
    title: "Admin Users",
    hideFromSitemap: true,
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.basicAccess,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin-properties",
    component: <AdminLegacyRedirect />,
    title: "Admin Properties",
    hideFromSitemap: true,
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin-ucc-status",
    component: <AdminLegacyRedirect />,
    title: "Admin UCC Status",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin-properties/:propertyId",
    component: <AdminLegacyRedirect />,
    title: "Admin Dashboard",
    hideFromSitemap: true,
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/admin-professionals",
    component: <AdminLegacyRedirect />,
    title: "Professionals",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-professionals/:professionalId",
    component: <Professional />,
    title: "Professionals",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-profile",
    component: <AdminProfile />,
    title: "Admin Profile",
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES?.provider,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
    allowedUCCAdmin: true,
  },
  {
    path: "/add-leads",
    component: <AdminLegacyRedirect />,
    title: "Add Leads",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.marketingAdmin],
  },
  {
    path: "/admin-survey",
    component: <AdminLegacyRedirect />,
    title: "Survey Questions",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.marketingAdmin],
  },

  {
    path: "/promocodes",
    component: <AdminLegacyRedirect />,
    title: "Promo Code",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.basicAccess],
  },
  {
    path: "/service-providers",
    component: <ServiceProviders />,
    title: "Service Providers",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/service-providers/*",
    component: <ServiceProviders />,
    title: "Service Providers",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-providers",
    component: <Providers />,
    title: "Providers",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-providers/:providerId",
    component: <Providers />,
    title: "Providers",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-county",
    component: <CountyAdmin />,
    title: "Admin Counties",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin-affiliate-company",
    component: <TitleCompany />,
    title: "Affiliate Companies",
    role: [ROLES.admin],
  },
  {
    path: "/admin-county/:countyId",
    component: <CountyAdmin />,
    title: "Providers",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admins",
    component: <AdminLegacyRedirect />,
    title: "Internal Admins",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admins/:adminID",
    component: <AdminLegacyRedirect />,
    title: "Admins",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/admin/internal/:adminID",
    component: <Admins />,
    title: "Admins",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/request-document-list",
    component: <RequestDocumentList />,
    title: "Requested Doucmnets List",
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/customer-service-queue",
    component: <CustomerServiceQueue />,
    title: "Customer Service Queue",
    role: [ROLES.admin, ROLES.subAdmin, ROLES.countyRecorder],
  },
  {
    path: "/admin-property-monitoring",
    component: <AdminLegacyRedirect />,
    title: "Property Monitoring",
    hideFromSitemap: true,
    role: [
      ROLES.admin,
      ROLES.subAdmin,
      ROLES.countyRecorder,
      ROLES.AffiliateManager,
    ],
  },
  {
    path: "/admin-recording-fees",
    component: <AdminLegacyRedirect />,
    title: "Recording Fees",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.basicAccess],
  },
  {
    path: "/admin-export-csv",
    component: <AdminLegacyRedirect />,
    title: "Export Payments",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.basicAccess],
  },
  {
    path: "/admin-logs",
    component: <AdminLegacyRedirect />,
    title: "Logs",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin, ROLES.basicAccess],
  },
  {
    path: "/admin-logs/:user_id",
    component: <AdminLogsList />,
    title: "Logs",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
  {
    path: "/add-new-users",
    component: <AdminLegacyRedirect />,
    title: "Add a new Users",
    hideFromSitemap: true,
    role: [ROLES.admin, ROLES.subAdmin],
  },
];

export const professionalRoutes = [
  {
    path: "/professional-dashboard",
    component: <ProfessionalDashboard />,
    title: "Professional Dashboard",
    role: [ROLES.professional],
  },
  {
    path: "/professional-profile",
    component: <ProfessionalsProfile />,
    title: "Professional Profile",
    role: [ROLES.professional],
  },
  {
    path: "/professional-users-list",
    component: <ProfessionalUsersList />,
    title: "Users",
    role: [ROLES.professional],
  },
  {
    path: "/send-invitation",
    component: <SendInvitation />,
    title: "Send Invitation",
    role: [ROLES.professional],
  },
  {
    path: "/professional-properties",
    component: <ProfessionalProperties />,
    title: "Professional Properties",
    role: [ROLES.professional],
  },
  {
    path: "/professional-subscriptions",
    component: <Subscriptions />,
    title: "Professional Subscription",
    role: [ROLES.professional],
  },
  {
    path: "/professional-document",
    component: <Documents />,
    title: "Professional Document",
    role: [ROLES.professional],
  },
  {
    path: "/professional-requested-documents",
    component: <DocumentRequest />,
    title: "Document Request",
    role: ROLES.professional,
  },
  {
    path: "/professional-logs",
    component: <LogsTable />,
    title: "Professional Logs",
    role: [ROLES.professional],
  },
  {
    path: "/professional-allow-access",
    component: <Spouse />,
    title: "Professional Allow Access",
    role: [ROLES.professional],
  },
  {
    path: "/professional-settings",
    component: <UserSettingIndex />,
    title: "Professional Settings",
    role: [ROLES.professional],
  },
];

export const serviceProviderRoutes = [
  // {
  //   path: "/",
  //   component: <ServiceProvider />,
  //   title: "Service Provider",
  //   role: "serviceProvider",
  //   domain: SUBDOMAIN,
  // },
  // {
  //   path: "/create-account",
  //   component: <ServiceProviderSignup />,
  //   title: "Create Account",
  //   // role: "serviceProvider",
  //   // hideFromSitemap: true,
  //   domain: SUBDOMAIN,
  // },
  // {
  //   path: "/login",
  //   component: <ServicersLogin />,
  //   title: "Login",
  //   // role: "serviceProvider",
  //   // hideFromSitemap: true,
  //   domain: SUBDOMAIN,
  // },
  {
    path: "/create-new-password",
    component: <ServicerCreateNewPass />,
    title: "Create New Password ",
    hideFromSitemap: true,
    domain: SUBDOMAIN,
  },
  {
    path: "/reset-password",
    component: <ServicerResetPassword />,
    title: "Forgot Password ",
    // hideFromSitemap: true,
    domain: SUBDOMAIN,
  },
  {
    path: "/setup-account",
    component: <SetupAccount />,
    title: "Set Up Account",
    hideFromSitemap: true,
    hideNavbar: true,
    domain: SUBDOMAIN,
  },
  {
    path: "/property-risk-management",
    component: <LeadGenDesktop />,
    title: "Property Risk Report ",
    hideFromSitemap: true,
    domain: SUBDOMAIN,
  },
  // {
  //   path: "/verify-otp-password",
  //   component: <ServicerVerifyOTP />,
  //   title: "Reset Password ",
  //   domain: SUBDOMAIN,
  // },
];

export const serviceProviderPublicRoutes = [
  // {
  //   path: "/",
  //   component: <CreativeLandingPage />,
  //   title: "Home",
  //   // role: ROLES.serviceProvider,
  //   isLandingPage: true,
  //   domain: SUBDOMAIN,
  // },
  {
    path: "/for-customer",
    component: <ForCustomer />,
    title: "Home",
    // role: ROLES.serviceProvider,
    domain: SUBDOMAIN,
    onClickEvent: true,
  },
  {
    path: "/privacy",
    component: <PrivacyPolicyPage />,
    title: "Privacy Policy",
    role: ROLES.serviceProvider,
    hideFromSitemap: true,
    domain: SUBDOMAIN,
  },
  // {
  //   path: "/terms",
  //   component: <TermsAndConditions />,
  //   title: "Terms & Conditions",
  //   role: ROLES.serviceProvider,
  //   hideFromSitemap: true,
  //   domain: SUBDOMAIN,
  // },
  {
    path: "/sitemap",
    component: <SiteMapPage />,
    title: "Sitemap",
    hideFromSitemap: true,
    domain: SUBDOMAIN,
  },
];

export const forServiceProvicerRoute = [
  {
    path: "/",
    component: <ForServiceProvider isTailwindComponent={isTailwindComponent} />,
    title: "Request Customer's Property Documnet",
    domain: SUBDOMAIN,
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/for-service-provider",
    component: <ForServiceProvider isTailwindComponent={isTailwindComponent} />,
    title: "Request Customer's Property Documnet",
    domain: SUBDOMAIN,
    hideFromSitemap: true,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/car-escrow-access",
    component: <CarEscrowAccessPage />,
    title: "Open closing assignment",
    domain: SUBDOMAIN,
    hideNavbar: true,
    footer: false,
  },
  {
    path: "/create-account",
    component: (
      <ServiceProviderSignup isTailwindComponent={isTailwindComponent} />
    ),
    title: "Create Account",
    domain: SUBDOMAIN,
    hideNavbar: isTailwindComponent,
    footer: !isTailwindComponent,
  },
  {
    path: "/login",
    component: <ServicersLogin isTailwindComponent={isTailwindComponent} />,
    title: "Login",
    // role: "serviceProvider",
    // hideFromSitemap: true,
    hideNavbar: isTailwindComponent,
    domain: SUBDOMAIN,
    footer: !isTailwindComponent,
  },
];

export const serviceProviderLoginRoutes = [
  {
    path: "/servicer-profile",
    component: <ServicerProfilePage />,
    title: "Profile",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  // {
  //   path: "/users",
  //   component: <ServicerUsers />,
  //   title: "Servicer Users",
  //   domain: SUBDOMAIN,
  //   role: SERVICE_PROVIDER_PORTAL_ROLES,
  // },
  {
    path: "/clients/create",
    component: <ServicerClientCreatePage />,
    title: "Add client",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/clients/:userId",
    component: <ServicerClientWorkspacePage />,
    title: "Client workspace",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/clients",
    component: <ServicerClientsEntry />,
    title: "Clients",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/car-portfolio/:propertyId",
    component: <CarAgentPortfolioDetailPage />,
    title: "C.A.R. property",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/closing-assignments",
    component: <CarClosingAssignmentsPage />,
    title: "Closing assignments",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/car-portfolio",
    component: <CarPortfolioRedirect />,
    title: "Clients",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/commissions",
    component: <ServicerCommissionsPage />,
    title: "Service fee's",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/fulfillment-assignments",
    component: <ServicerFulfillmentAssignmentsPage />,
    title: "Territory assignments",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/fulfillment/clients/:userId",
    component: <ServicerFulfillmentUccWorkspacePage />,
    title: "Territory UCC workspace",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/documents",
    component: (
      <ServicerPropertyRequestsPage isTailwindComponent={isTailwindComponent} />
    ),
    title: "Unlock Request(s)",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/servicer-properties",
    component: <ServicerPropertiesPage />,
    title: "Properties",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/servicer-subscriptions",
    component: <ServicerSubscriptionsPage />,
    title: "Subscriptions",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/servicer-scans",
    component: <ServicerScansPage />,
    title: "Scans",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/request-document",
    component: (
      <RequestDocumentForm isTailwindComponent={isTailwindComponent} />
    ),
    title: "Properties",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/search-property",
    component: (
      <ServicerSearchProperty isTailwindComponent={isTailwindComponent} />
    ),
    title: "Search Property",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/risk-assessments",
    component: <ServicerRiskAssessmentsPage />,
    title: "Risk Assessments",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  ...(JESSE_REDACT_ENABLED
    ? [
        {
          path: "/jesse-protected-parties",
          component: (
            <ServicerJesseProtectedSearch
              isTailwindComponent={isTailwindComponent}
            />
          ),
          title: "Protected Parties",
          domain: SUBDOMAIN,
          role: SERVICE_PROVIDER_PORTAL_ROLES,
        },
      ]
    : []),
  {
    path: "/add-protection",
    component: <ServicerLegacyClientsRedirect to="/clients/create" />,
    title: "Add Protection",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/client-onboarding/:userId",
    component: <ServicerLegacyClientsRedirect to="/clients/:userId" />,
    title: "Client onboarding",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/client-onboarding",
    component: <ServicerLegacyClientsRedirect />,
    title: "Client onboarding",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/servicer-documents",
    component: <ServicerDocumentsPage />,
    title: "Documents",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },

  {
    path: "/servicer-logs",
    component: <ServicerLogsPage />,
    title: "Logs",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/servicer-allow-access",
    component: <ServicerAllowAccessPage />,
    title: "Allow Access",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
  {
    path: "/servicer-requested-documents",
    component: <ServicerRequestedDocumentsPage />,
    title: "Requested Documents",
    domain: SUBDOMAIN,
    role: SERVICE_PROVIDER_PORTAL_ROLES,
  },
];

const CAR_CUSTOMER_AUTH_PATHS = new Set([
  "/login",
  "/auth/callback",
  "/:prefix/login",
  "/setup-account",
  "/partner-activate",
  "/:prefix/setup-account",
  "/create-account",
  "/:county_name/create-account",
  "/create-account/*",
]);

const carServiceProviderSignup = (
  <ServiceProviderSignup
    isTailwindComponent={isTailwindComponent}
    defaultTypeName={CAR_REAL_ESTATE_AGENT_TYPE}
  />
);

// CAR host pages use the servicer navbar (Home + For Service Providers). Home
// and the logo open the EquityProtect site, as on the servicer site.
export const CAR_HEADER_PROPS = {
  isServiceProvider: true,
};

// General pricing lists non-CAR tiers and prices; CAR packages live on "/".
const CAR_LANDING_REDIRECT_PATHS = new Set(["/pricing"]);

export const carHostRoutes = [
  {
    path: "/",
    component: <CarMemberPartnership />,
    title: "C.A.R. Member Property Protection",
  },
  {
    path: "/create-account",
    component: carServiceProviderSignup,
    title: "Create Account",
    hideNavbar: Boolean(isTailwindComponent),
    hideFooter: Boolean(isTailwindComponent),
  },
  {
    path: "/CAR-Signup",
    component: carServiceProviderSignup,
    title: "Create Account",
    hideNavbar: Boolean(isTailwindComponent),
    hideFooter: Boolean(isTailwindComponent),
  },
  {
    path: "/login",
    component: <ServicersLogin isTailwindComponent={isTailwindComponent} />,
    title: "Login",
    hideNavbar: Boolean(isTailwindComponent),
    hideFooter: Boolean(isTailwindComponent),
  },
  {
    path: "/reset-password",
    component: <ServicerResetPassword />,
    title: "Forgot Password",
    hideNavbar: true,
    hideFooter: true,
  },
  {
    path: "/create-new-password",
    component: <ServicerCreateNewPass />,
    title: "Create New Password",
    hideNavbar: true,
    hideFooter: true,
  },
  ...[...CAR_LANDING_REDIRECT_PATHS].map((path) => ({
    path,
    component: <Navigate to="/" replace />,
    title: "C.A.R. Member Property Protection",
    hideNavbar: true,
    hideFooter: true,
  })),
  ...publicRoutesV2.filter(
    (route) =>
      !CAR_CUSTOMER_AUTH_PATHS.has(route.path) &&
      !CAR_LANDING_REDIRECT_PATHS.has(route.path),
  ),
  ...blogRoutesV2,
  publicRoutes.find((route) => route.path === "/for-service-provider"),
].filter(Boolean);

export const tryDomainRoutes = [
  {
    path: "/",
    component: <LeadPage />,
    title: "Lead Page",
  },
];

export const lannerPublicRoutes = [
  {
    path: "/",
    component: (
      <>
        <Homepage />
      </>
    ),
    title: "Lenner Accoount",
  },
  {
    path: ":provider_type",
    component: (
      <>
        <Homepage />
      </>
    ),
    title: "Lenner Accoount",
  },
  {
    path: ":provider_type/create-account",
    component: (
      <>
        <LennarCreateAccount />
      </>
    ),
    title: "Lenner Accoount",
  },
  {
    path: "/create-account",
    component: (
      <>
        <LennarCreateAccount />
      </>
    ),
    title: "Lenner Accoount",
  },
  {
    path: ":provider_type/do-payment",
    component: (
      <>
        <>
          {/* <Elements stripe={stripePromise}> */}
          <LennarPropertyList />
          {/* </Elements> */}
        </>
      </>
    ),
    title: "Lennar Subscription",
    role: [ROLES.lennar],
  },
  {
    path: "/do-payment",
    component: (
      <>
        <>
          {/* <Elements stripe={stripePromise}> */}
          <LennarPropertyList />
          {/* </Elements> */}
        </>
      </>
    ),
    title: "Lennar Subscription",
    role: [ROLES.lennar],
  },
  {
    path: ":provider_type/cancellation-policy",
    component: (
      <>
        <CancellationPolicy />
      </>
    ),
    title: "Cancellation Policy",
    role: [ROLES.lennar],
  },
  {
    path: "/cancellation-policy",
    component: (
      <>
        <CancellationPolicy />
      </>
    ),
    title: "Cancellation Policy",
    role: [ROLES.lennar],
  },
  {
    path: ":provider_type/payment-done",
    component: (
      <>
        <PaymentDone />
      </>
    ),
    title: "Payment Done",
    role: [ROLES.lennar],
  },
  {
    path: "/payment-done",
    component: (
      <>
        <PaymentDone />
      </>
    ),
    title: "Payment Done",
    role: [ROLES.lennar],
  },
];

export const lannerRestrictedRoutes = [
  // {
  //   path: "/payment-done",
  //   component: (
  //     <>
  //       <PaymentDone />
  //     </>
  //   ),
  //   title: "Payment Done",
  //   role: [ROLES.lennar],
  // },
];

export const ROUTESFORSITEMAP = [
  ...publicRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),
  ...publicRoutesV2?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),
  ...blogRoutesV2?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...userAdminRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...loggedInUserRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...adminRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...professionalRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  { sevicepovidrRoute: "start" },

  ...serviceProviderRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...serviceProviderPublicRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...forServiceProvicerRoute?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...serviceProviderLoginRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  { tryRoute: "start" },
  ...tryDomainRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  { lennar: "start" },
  ...lannerPublicRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),

  ...lannerRestrictedRoutes?.map((route) => ({
    url: route?.path,
    lastmod: new Date().toISOString(),
  })),
];
